import {
  IndigoIndigo60,
  InputSelectControl,
  Link,
  SettingsFormControl,
  SettingsGroup
} from '@sequencehq/core-components'

import { BASE_INPUT_FIELD_STYLES } from '../../settings.constants'
import type { RevenueRecognitionSettingsControls } from './revenueRecognitionSettings.types'

type AllocationStrategy =
  | 'PRORATE_DAILY'
  | 'BALANCE_EVEN_MONTHLY'
  | 'PRORATE_MONTHLY'

const ALLOCATION_STRATEGY_DOCS_URL =
  'https://docs.sequencehq.com/revenue-recognition/settings#allocation-strategy'

// All strategies recognize the same total and give every full month an equal
// amount; they differ only in how the part-months at the start and end of a
// service period are prorated. Worked breakdowns live in the linked docs.
const ALLOCATION_STRATEGY_OPTIONS: {
  label: string
  value: AllocationStrategy
}[] = [
  { label: 'Daily rate (default)', value: 'PRORATE_DAILY' },
  { label: 'Even months, balance on last', value: 'BALANCE_EVEN_MONTHLY' },
  { label: 'Even months, prorate both ends', value: 'PRORATE_MONTHLY' }
]

export const AllocationStrategySettings = (
  props: RevenueRecognitionSettingsControls
) => {
  const handleChange = (value: string) => {
    const settings = props.settings

    if (!settings || props.isUpdating) {
      return
    }

    // The PUT is a full replacement, so the other settings must be sent
    // alongside the strategy or the server resets them to their defaults.
    props.updateSettings({
      recognizeRevenueOnAccountingDate:
        settings.recognizeRevenueOnAccountingDate,
      allocationPartialProrationStrategy: value as AllocationStrategy,
      integrationsJournalPostMode: settings.integrationsJournalPostMode
    })
  }

  return (
    <SettingsGroup
      title="Allocation"
      isLoading={props.isLoading}
      footer={
        <SettingsGroup.Footer>
          The allocation strategy controls how straight-line revenue is spread
          across the service period. Changes apply only to new invoices and new
          schedules. For more information, see our docs on{' '}
          <Link
            to={ALLOCATION_STRATEGY_DOCS_URL}
            isExternal
            color={IndigoIndigo60}
          >
            allocation strategies
          </Link>
          .
        </SettingsGroup.Footer>
      }
    >
      <SettingsFormControl
        label="Allocation strategy"
        htmlFor="allocation-strategy"
        showBorderBottom={false}
      >
        <InputSelectControl
          id="allocation-strategy"
          data-testid="revenueRecognition.settings.allocationStrategy"
          placeholder="Select allocation strategy"
          disabled={!props.settings || props.isUpdating}
          triggerStyle={{ width: BASE_INPUT_FIELD_STYLES.width }}
          options={ALLOCATION_STRATEGY_OPTIONS}
          initialValue={props.settings?.allocationPartialProrationStrategy}
          onChange={handleChange}
        />
      </SettingsFormControl>
    </SettingsGroup>
  )
}
